import { deepEqual as equal } from 'fast-equals'
import { MakeLogicType, actions, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'

import { SelectedQuickFilter, quickFiltersSectionLogic } from 'lib/components/QuickFilters'
import { isUniversalGroupFilterLike } from 'lib/components/UniversalFilters/utils'
import { Params } from 'scenes/sceneTypes'

import { DateRange, QuickFilterContext } from '~/queries/schema/schema-general'
import { FilterLogicalOperator, PropertyFilterType, UniversalFiltersGroup } from '~/types'

import { updateSearchParams } from '../../utils'

export const DEFAULT_DATE_RANGE = { date_from: '-7d', date_to: null }
export const DEFAULT_FILTER_GROUP = {
    type: FilterLogicalOperator.And,
    values: [{ type: FilterLogicalOperator.And, values: [] }],
}
export const DEFAULT_TEST_ACCOUNT = false
const DEFAULT_SEARCH_QUERY = ''

export interface IssueFiltersLogicProps {
    logicKey: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueFiltersLogicValues {
    selectedQuickFilters: Record<string, SelectedQuickFilter> // quickFiltersSectionLogic
    dateRange: DateRange
    filterGroup: UniversalFiltersGroup
    filterTestAccounts: boolean
    mergedFilterGroup: UniversalFiltersGroup
    searchInput: string
    searchQuery: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueFiltersLogicActions {
    setDateRange: (dateRange: DateRange) => {
        dateRange: DateRange
    }
    setFilterGroup: (filterGroup: UniversalFiltersGroup) => {
        filterGroup: UniversalFiltersGroup
    }
    setFilterTestAccounts: (filterTestAccounts: boolean) => {
        filterTestAccounts: boolean
    }
    setSearchInput: (searchInput: string) => {
        searchInput: string
    }
    setSearchQuery: (searchQuery: string) => {
        searchQuery: string
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issueFiltersLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        mergedFilterGroup: (
            filterGroup: UniversalFiltersGroup,
            selectedQuickFilters: Record<string, SelectedQuickFilter>
        ) => UniversalFiltersGroup
    }
}

export type issueFiltersLogicType = MakeLogicType<
    issueFiltersLogicValues,
    issueFiltersLogicActions,
    IssueFiltersLogicProps,
    issueFiltersLogicMeta
>

export const issueFiltersLogic = kea<issueFiltersLogicType>([
    path(['products', 'error_tracking', 'components', 'IssueFilters', 'issueFiltersLogic']),
    props({} as IssueFiltersLogicProps),
    key(({ logicKey }) => logicKey),

    connect((props: IssueFiltersLogicProps) => ({
        values: [
            quickFiltersSectionLogic({
                context: QuickFilterContext.ErrorTrackingIssueFilters,
                logicKey: props.logicKey,
            }),
            ['selectedQuickFilters'],
        ],
    })),

    actions({
        setDateRange: (dateRange: DateRange) => ({ dateRange }),
        setSearchInput: (searchInput: string) => ({ searchInput }),
        setSearchQuery: (searchQuery: string) => ({ searchQuery }),
        setFilterGroup: (filterGroup: UniversalFiltersGroup) => ({ filterGroup }),
        setFilterTestAccounts: (filterTestAccounts: boolean) => ({ filterTestAccounts }),
    }),
    reducers({
        dateRange: [
            DEFAULT_DATE_RANGE as DateRange,
            {
                setDateRange: (_, { dateRange }) => dateRange,
            },
        ],
        filterGroup: [
            DEFAULT_FILTER_GROUP as UniversalFiltersGroup,
            {
                setFilterGroup: (_, { filterGroup }) =>
                    filterGroup?.values?.length ? filterGroup : DEFAULT_FILTER_GROUP,
            },
        ],
        filterTestAccounts: [
            DEFAULT_TEST_ACCOUNT as boolean,
            {
                setFilterTestAccounts: (_, { filterTestAccounts }) => filterTestAccounts,
            },
        ],
        searchInput: [
            DEFAULT_SEARCH_QUERY as string,
            {
                setSearchInput: (_, { searchInput }) => searchInput,
                setSearchQuery: (_, { searchQuery }) => searchQuery,
            },
        ],
        searchQuery: [
            DEFAULT_SEARCH_QUERY as string,
            {
                setSearchQuery: (_, { searchQuery }) => searchQuery,
            },
        ],
    }),
    listeners(({ actions, values }) => ({
        setSearchInput: async ({ searchInput }, breakpoint) => {
            await breakpoint(250)
            if (values.searchInput === searchInput) {
                actions.setSearchQuery(searchInput)
            }
        },
    })),
    selectors({
        mergedFilterGroup: [
            (s) => [s.filterGroup, s.selectedQuickFilters],
            (
                filterGroup: UniversalFiltersGroup,
                selectedQuickFilters: Record<string, SelectedQuickFilter>
            ): UniversalFiltersGroup => {
                let omnisearchFilters: any[] = []
                let omnisearchType: FilterLogicalOperator = FilterLogicalOperator.And
                if (
                    !!filterGroup.values &&
                    Array.isArray(filterGroup.values) &&
                    filterGroup.values.length > 0 &&
                    isUniversalGroupFilterLike(filterGroup.values[0])
                ) {
                    omnisearchFilters = filterGroup.values[0].values
                    omnisearchType = filterGroup.values[0].type ?? FilterLogicalOperator.And
                }

                const filtersFromQuickFilters = Object.values(selectedQuickFilters).map((qf: SelectedQuickFilter) => {
                    const filterValue = qf.value === null ? undefined : Array.isArray(qf.value) ? qf.value : [qf.value]

                    return {
                        type: PropertyFilterType.Event,
                        key: qf.propertyName,
                        operator: qf.operator,
                        ...(filterValue !== undefined && { value: filterValue }),
                    }
                })

                // Quick filters always AND with the user's omnisearch group. When the user picked OR
                // and there are quick filters, nest the omnisearch group so its OR is preserved while
                // ANDing with the quick filters.
                const shouldNestOmnisearch =
                    omnisearchType === FilterLogicalOperator.Or &&
                    omnisearchFilters.length > 1 &&
                    filtersFromQuickFilters.length > 0

                const innerValues = shouldNestOmnisearch
                    ? [{ type: FilterLogicalOperator.Or, values: omnisearchFilters }, ...filtersFromQuickFilters]
                    : [...omnisearchFilters, ...filtersFromQuickFilters]

                const innerType = filtersFromQuickFilters.length === 0 ? omnisearchType : FilterLogicalOperator.And

                return {
                    type: FilterLogicalOperator.And,
                    values: [
                        {
                            type: innerType,
                            values: innerValues,
                        },
                    ],
                } as UniversalFiltersGroup
            },
        ],
    }),
])

export interface IssueFilterValues {
    dateRange: DateRange | null
    filterGroup: UniversalFiltersGroup
    filterTestAccounts: boolean
    searchQuery: string
}

export interface IssueFilterActions {
    setDateRange: (dateRange: DateRange) => void
    setSearchQuery: (searchQuery: string) => void
    setFilterGroup: (filterGroup: UniversalFiltersGroup) => void
    setFilterTestAccounts: (filterTestAccounts: boolean) => void
}

export function updateFilterSearchParams(params: Params, values: IssueFilterValues): Params {
    updateSearchParams(params, 'filterTestAccounts', values.filterTestAccounts, DEFAULT_TEST_ACCOUNT)
    updateSearchParams(params, 'searchQuery', values.searchQuery, DEFAULT_SEARCH_QUERY)
    updateSearchParams(params, 'filterGroup', values.filterGroup, DEFAULT_FILTER_GROUP)
    updateSearchParams(params, 'dateRange', values.dateRange, DEFAULT_DATE_RANGE)
    return params
}

export const triggerFilterActions = (params: Params, values: any, actions: IssueFilterActions): void => {
    const dateRange = params.dateRange ?? DEFAULT_DATE_RANGE
    if (!equal(dateRange, values.dateRange)) {
        actions.setDateRange(dateRange)
    }

    const filterGroup = params.filterGroup ?? DEFAULT_FILTER_GROUP
    if (!equal(filterGroup, values.filterGroup)) {
        actions.setFilterGroup(filterGroup)
    }

    const filterTestAccounts = params.filterTestAccounts ?? DEFAULT_TEST_ACCOUNT
    if (!equal(filterTestAccounts, values.filterTestAccounts)) {
        actions.setFilterTestAccounts(filterTestAccounts)
    }

    const newQuery = params.searchQuery ? params.searchQuery.toString() : DEFAULT_SEARCH_QUERY
    if (!equal(newQuery, values.searchQuery)) {
        actions.setSearchQuery(newQuery)
    }
}
